import React from "react";
import { Box, Container, Typography, Grid } from "@mui/material";
import { Message, Store } from "@mui/icons-material";
import { motion } from "framer-motion";
import AnimatedSection from "../AnimatedSection";
import { gradients } from "../../../utils/theme";

/**
 * HowItWorksSection Component
 *
 * Displays the 3-step process of how ThruChat works.
 */
const HowItWorksSection: React.FC = () => {
  const steps = [
    {
      icon: Message,
      gradient: gradients.navyLux,
      step: "Step 1",
      title: "Instant Parking",
      description: "WhatsApp message, UPI payment, QR ticket in seconds",
      delay: 0,
    },
    {
      icon: Store,
      gradient: gradients.gold,
      step: "Step 2",
      title: "Digital Mall Guide",
      description:
        "Explore stores, offers, events & directions - all on WhatsApp",
      delay: 0.2,
    },
    {
      icon: Message,
      gradient: "linear-gradient(135deg, #1a237e 0%, #3949ab 100%)",
      step: "Step 3",
      title: "Share Feedback",
      description: "Rate your visit via WhatsApp link",
      delay: 0.4,
    },
  ];

  return (
    <Box id="how-it-works" sx={{ py: { xs: 4, sm: 5, md: 6, lg: 8 } }}>
      <Container maxWidth="lg">
        <AnimatedSection>
          <Typography
            variant="h2"
            textAlign="center"
            sx={{
              fontSize: {
                xs: "2rem",
                sm: "2.25rem",
                md: "2.5rem",
                lg: "2.75rem",
              },
              fontWeight: 700,
              mb: 2,
              background: gradients.navyLux,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            How MallInHand Works
          </Typography>
          <Typography
            variant="h6"
            textAlign="center"
            color="text.secondary"
            sx={{ mb: 6, maxWidth: "800px", mx: "auto" }}
          >
            Instant parking, digital mall guide, exclusive deals & events - all
            through WhatsApp
          </Typography>
        </AnimatedSection>

        <Grid container spacing={4} alignItems="center">
          {steps.map((step, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <AnimatedSection delay={step.delay}>
                <motion.div
                  whileHover={{ y: -10, scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                >
                  <Box sx={{ textAlign: "center", position: "relative" }}>
                    {/* Connecting line for desktop */}
                    {index < 2 && (
                      <Box
                        sx={{
                          display: { xs: "none", md: "block" },
                          position: "absolute",
                          top: 40,
                          left: "calc(50% + 40px)",
                          width: "calc(100% - 80px)",
                          height: 2,
                          background:
                            "linear-gradient(90deg, rgba(102, 126, 234, 0.3), rgba(102, 126, 234, 0.1))",
                          zIndex: 0,
                        }}
                      />
                    )}
                    <motion.div
                      animate={{
                        scale: [1, 1.1, 1],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        delay: index * 0.7,
                      }}
                      whileHover={{
                        scale: 1.15,
                        rotate: [0, -5, 5, -5, 0],
                      }}
                    >
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: "50%",
                          background: step.gradient,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mx: "auto",
                          mb: 2,
                          boxShadow: 3,
                          position: "relative",
                          zIndex: 1,
                          transition: "all 0.3s ease",
                          "&:hover": {
                            boxShadow: "0 8px 30px rgba(102, 126, 234, 0.4)",
                          },
                        }}
                      >
                        <motion.div
                          whileHover={{ rotate: 360 }}
                          transition={{ duration: 0.6 }}
                        >
                          <step.icon sx={{ fontSize: 40, color: "white" }} />
                        </motion.div>
                      </Box>
                    </motion.div>
                    <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
                      {step.step}
                    </Typography>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      {step.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {step.description}
                    </Typography>
                  </Box>
                </motion.div>
              </AnimatedSection>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default HowItWorksSection;
